import React from 'react'

import CheckboxDropdown from '../../atoms/CheckboxDropdown'
import UserOption from '../../atoms/Dropdown/users'
import SingleUser from './SingleUser'

import { sortByName, uniqueArray } from '../../../utilities'
import Signal from '@SonarCX/lib/signal'
import { addRecentUser, getRecentUsers } from '@SonarCX/lib/idb'
import { DEFAULT_EMBEDS, useUserAPI } from '../../../providers/UserAPIProvider'
import useDelayedUserSearch, {
  DEFAULT_FILTER
} from '../../../hooks/useDelayedUserSearch'
import { useEnvironment } from '../../../providers/EnvironmentProvider'

const UserSearchDropdown = ({
  multi = false,
  external = false,
  hideMe = false,
  extraEmbeds = [],
  onSelect = id => {},
  ...props
}) => {
  const { allUsers, getUsers, updateUsers, loadUser } = useUserAPI()
  const {
    search: handleSearch,
    results: foundUsers,
    loading
  } = useDelayedUserSearch(Boolean(external), DEFAULT_FILTER, [
    ...DEFAULT_EMBEDS,
    ...extraEmbeds
  ])
  const { user: currentUser, env } = useEnvironment()

  const [search, setSearch] = React.useState('')
  const [recents, setRecents] = React.useState([])
  const [loadedInitialRecents, setLoadedInitialRecents] = React.useState(false)

  // Perform a search on system users
  React.useEffect(() => {
    handleSearch(search)
  }, [handleSearch, search])

  // Update user list with new users from search
  React.useEffect(() => {
    updateUsers(foundUsers)
  }, [updateUsers, foundUsers])

  // Load recent users
  React.useEffect(() => {
    recents.forEach(id => {
      loadUser(id)
    })
  }, [recents, loadUser])

  // Get recent users from IDB
  React.useEffect(() => {
    if (!env?.name) {
      return
    }

    const getStoredRecents = async () => {
      const storedRecents = await getRecentUsers({
        envName: env.name,
        userType: external
      })
      setRecents(storedRecents)
    }

    if (!loadedInitialRecents) {
      getStoredRecents()
      setLoadedInitialRecents(true)
    }

    window.SONAR_SIGNAL.on(Signal.recentUsers, getStoredRecents)

    return () => {
      window.SONAR_SIGNAL.off(Signal.recentUsers, getStoredRecents)
    }
  }, [env, loadedInitialRecents, external])

  const options = React.useMemo(() => {
    // Puts your user at the top of the list
    const biasedSort = (a, b) => {
      const myID = currentUser?.id
      if (!myID) {
        return 0
      }

      // Sort yourself ahead
      if (a === myID) {
        return -1
      } else if (b === myID) {
        return 1
      }

      // Ignore others
      return 0
    }

    let userIDs = Object.values(getUsers(external))
      .sort(sortByName)
      .map(u => u.id)
    if (hideMe) {
      userIDs = userIDs.filter(id => id !== currentUser?.id)
    }

    // Add recents to the top of the list before sorting
    userIDs = [...recents, ...userIDs]

    const sorted = userIDs.sort(biasedSort)
    return uniqueArray(sorted)
  }, [getUsers, external, currentUser, hideMe, recents])

  const renderOption = React.useCallback(
    id => {
      const opt = allUsers[id]
      if (opt) {
        return <UserOption key={id} id={id} opt={opt} />
      }
      return null
    },
    [allUsers]
  )

  const handleSelect = React.useCallback(
    id => {
      onSelect(id)

      if (!env?.name || !id) {
        return
      }

      addRecentUser({ envName: env.name, userType: external, userID: id })
    },
    [onSelect, env, external]
  )

  const ddProps = React.useMemo(
    () => ({
      options,
      renderOption,
      onSearch: setSearch,
      loading,
      onSelect: handleSelect
    }),
    [options, renderOption, setSearch, loading, handleSelect]
  )

  if (multi) {
    return <CheckboxDropdown {...ddProps} {...props} />
  } else {
    return <SingleUser {...ddProps} {...props} />
  }
}

export default UserSearchDropdown
